import React, { useState } from 'react';
import styled from 'styled-components';
import { useProjectElementListStore } from '../../../shared/store/projectElementStore';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useValidation } from '../../../shared/hooks/useValidation';
import { useStandardAlertStore } from '../../../shared/store/portal/alertStore';
import { getAlertMessage } from '../../../shared/constants/alertMessages';
import { openNewTab } from '../../../shared/util/openNewTab';
import { AlertPosition, AlertType, DisplayAlignment, DisplaySize } from '../../../shared/enum/EnumRepository';
import { WorkDetailData } from '../../../shared/dto/EntityRepository';
import { StyledImgProps } from '../../../shared/dto/StyleCompRepository';
import { convertS3UrlToCloudFrontUrl } from '../../../shared/aws/awsHelper';
import OptimizedMoleculeImg from '../../../shared/component/molecule/OptimizedMoleculeImg';
import { parseSupportedVideoUrl } from '../../../shared/util/videoUrl';
import UniversalIconBtn from '../../../shared/component/headless/button/UniversalIconBtn';
import playIcon from '../../../asset/icon/play.png';
export interface DetailVideoInPeMobileProps {
  peId: string;
  alignment: DisplayAlignment;
  displaySize: DisplaySize;
  data: WorkDetailData;
  priority: boolean;
}

const DetailVideoInPeMobile: React.FC<DetailVideoInPeMobileProps> = ({
  peId,
  alignment,
  displaySize,
  data,
  priority,
}) => {
  const handleImageChange = useProjectElementListStore((s) => s.updateImage);
  const { isEditMode } = useEditMode();
  const { isValidVideoUrl } = useValidation();
  const { enqueueAlert } = useStandardAlertStore();
  const [isHovered, setIsHovered] = useState(false);

  const convertedCloudFrontUrl = convertS3UrlToCloudFrontUrl(data.uploadFile.originUrl);

  const moveToVideoUrl = () => {
    if (!data.videoUrl) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('VIDEO_URL_NULL'),
      });
      return;
    }
    if (!isValidVideoUrl(data.videoUrl)) return;
    const videoUrl = parseSupportedVideoUrl(data.videoUrl);
    if (!videoUrl) return;
    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_VIDEO_URL_PLATFORM', { platform: videoUrl.platform }),
      callBack: () => openNewTab(videoUrl.url.href),
    });
  };

  return (
    <ImgInPeWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <ImageWrap>
        <OptimizedMoleculeImg
          srcUrl={convertedCloudFrontUrl}
          alt={data.description || 'Detail Cut'}
          displaySize={displaySize}
          displayAlignment={alignment || DisplayAlignment.CENTER}
          handleChange={(originUrl: string, width: number, height: number) =>
            handleImageChange(peId, originUrl, width, height)
          }
          StyledImg={WorkImageForLandscape as React.ComponentType<StyledImgProps>}
          priority={priority}
        />
        {!isEditMode && (
          <PlayOverlay $isHovered={isHovered}>
            <UniversalIconBtn
              size="standard"
              border={false}
              backgroundImg={playIcon}
              onClick={moveToVideoUrl}
            />
          </PlayOverlay>
        )}
      </ImageWrap>
    </ImgInPeWrapper>
  );
};

const ImgInPeWrapper = styled.div`
  margin: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 0;
`;

const ImageWrap = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PlayOverlay = styled.div<{ $isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  backdrop-filter: blur(${({ $isHovered }) => ($isHovered ? 1.5 : 0)}px);
  transition: all 0.4s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WorkImageForLandscape = styled.img<{ $displaySize: DisplaySize }>`
  width: ${({ $displaySize }) => {
    switch ($displaySize) {
      case DisplaySize.SMALL:
      case DisplaySize.REGULAR:
      case DisplaySize.MEDIUM:
      case DisplaySize.BIG:
        return '90vw';
      case DisplaySize.WIDE:
      default:
        return '100vw';
    }
  }};
  transition: width 0.6s ease;
  object-fit: contain;
`;

export default DetailVideoInPeMobile;
